import * as Sentry from "@sentry/angular";
import {
    ApplicationConfig,
    importProvidersFrom,
    ErrorHandler,
    provideAppInitializer,
    inject,
} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {IonicStorageModule} from '@ionic/storage-angular';
import {CoreModule} from '@core/core.module';
import { RouteReuseStrategy, Router, provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import {routes} from './app.routes';
import {SettledPreloadingStrategy} from '@core/routing/settled-preloading.strategy';
import {getApp, initializeApp, provideFirebaseApp} from '@angular/fire/app';
import {environment} from '../environments/environment';
import {getAnalytics, provideAnalytics} from '@angular/fire/analytics';
import {connectAuthEmulator, getAuth, indexedDBLocalPersistence, initializeAuth, provideAuth} from '@angular/fire/auth';
import {Capacitor} from '@capacitor/core';
import {connectFirestoreEmulator, getFirestore, provideFirestore} from '@angular/fire/firestore';
import {getMessaging, provideMessaging} from '@angular/fire/messaging';
import {getRemoteConfig, provideRemoteConfig} from '@angular/fire/remote-config';
import {getStorage, provideStorage} from '@angular/fire/storage';

export const appConfig: ApplicationConfig = {
    providers: [
    importProvidersFrom(
        BrowserModule,
        IonicModule.forRoot(),
        IonicStorageModule.forRoot(),
        CoreModule,
    ),
    provideRouter(routes, withComponentInputBinding(), withPreloading(SettledPreloadingStrategy)), 
    {
        provide: RouteReuseStrategy, 
        useClass: IonicRouteStrategy
    }, 
    provideFirebaseApp(() => initializeApp(environment.firebase)), 
    provideAnalytics(() => getAnalytics()), 
    provideAuth(() => {
        if (Capacitor.isNativePlatform()) {
            return initializeAuth(getApp(), {
                persistence: indexedDBLocalPersistence
            });
        } else {
            const fireAuth = getAuth();
            if (environment.useEmulators) {
                connectAuthEmulator(fireAuth, 'http://localhost:9099');
            }
            return fireAuth;
        }
    }),
    provideFirestore(() => {
        const firestore = getFirestore();
        if (environment.useEmulators) {
            connectFirestoreEmulator(firestore, 'localhost', 8080);
        }
        return firestore;
    }), 
    provideMessaging(() => getMessaging()), 
    provideRemoteConfig(() => getRemoteConfig()), 
    provideStorage(() => getStorage()),
    {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler()
    }, 
    {
        provide: Sentry.TraceService,
        deps: [Router]
    }, 
    provideAppInitializer(() => {
        inject(Sentry.TraceService);
    })
   ],
};